import { useI18n } from 'src/hooks/useI18n';

/**
 * Custom hook for type validation messages with i18n support
 * This hook provides translated validation error messages for type definitions
 */
export const useTypeValidationMessages = () => {
  const { t } = useI18n();

  return {
    // Field validation messages
    field: {
      refinancingCurrentProperty: () =>
        t('types.validation.field.refinancingCurrentProperty'),
      hasRealtor: () => t('types.validation.field.hasRealtor'),
      needsRealtor: () => t('types.validation.field.needsRealtor'),
      hasOwnershipInterest: () =>
        t('types.validation.field.hasOwnershipInterest'),
      sameAddressAsBorrower: () =>
        t('types.validation.field.sameAddressAsBorrower'),
      sameMailingAddressAsBorrower: () =>
        t('types.validation.field.sameMailingAddressAsBorrower'),
      hasCoborrowerOwnershipInterest: () =>
        t('types.validation.field.hasCoborrowerOwnershipInterest'),
    },

    // Loan validation messages
    loan: {
      idealPurchasePrice: () => t('types.validation.loan.idealPurchasePrice'),
      mortgageType: () => t('types.validation.loan.mortgageType'),
      borrowerRequestedLoanAmount: () =>
        t('types.validation.loan.borrowerRequestedLoanAmount'),
      cashOutBalance: () => t('types.validation.loan.cashOutBalance'),
      downPaymentAmount: () => t('types.validation.loan.downPaymentAmount'),
      downPaymentSourceDescription: () =>
        t('types.validation.loan.downPaymentSourceDescription'),
      downPaymentPercent: () => t('types.validation.loan.downPaymentPercent'),
      purchasePriceAmount: () => t('types.validation.loan.purchasePriceAmount'),
      loanAmortizationType: () =>
        t('types.validation.loan.loanAmortizationType'),
      loanAmortizationTermMonths: () =>
        t('types.validation.loan.loanAmortizationTermMonths'),
      propertyUsageType: () => t('types.validation.loan.propertyUsageType'),
      fnmProductPlanIdentifier: () =>
        t('types.validation.loan.fnmProductPlanIdentifier'),
    },

    // Property validation messages
    property: {
      addressUnitDesignatorType: () =>
        t('types.validation.property.addressUnitDesignatorType'),
      addressUnitIdentifier: () =>
        t('types.validation.property.addressUnitIdentifier'),
      addressLineText: () => t('types.validation.property.addressLineText'),
      city: () => t('types.validation.property.city'),
      state: () => t('types.validation.property.state'),
      postalCode: () => t('types.validation.property.postalCode'),
      county: () => t('types.validation.property.county'),
      financedNumberOfUnits: () =>
        t('types.validation.property.financedNumberOfUnits'),
      refinancePropertyAcquiredYear: () =>
        t('types.validation.property.refinancePropertyAcquiredYear'),
      loanPurposeType: () => t('types.validation.property.loanPurposeType'),
      refinancePropertyExistingLienAmount: () =>
        t('types.validation.property.refinancePropertyExistingLienAmount'),
    },

    // Loan product data validation messages
    loanProductData: {
      gsePropertyType: () =>
        t('types.validation.loanProductData.gsePropertyType'),
    },

    // Type labels
    unitTypes: {
      apartment: () => t('types.unitTypes.apartment'),
      basement: () => t('types.unitTypes.basement'),
      building: () => t('types.unitTypes.building'),
      condo: () => t('types.unitTypes.condo'),
      department: () => t('types.unitTypes.department'),
      floor: () => t('types.unitTypes.floor'),
      front: () => t('types.unitTypes.front'),
      hangar: () => t('types.unitTypes.hangar'),
      key: () => t('types.unitTypes.key'),
      lobby: () => t('types.unitTypes.lobby'),
      lot: () => t('types.unitTypes.lot'),
      lower: () => t('types.unitTypes.lower'),
      office: () => t('types.unitTypes.office'),
      penthouse: () => t('types.unitTypes.penthouse'),
      pier: () => t('types.unitTypes.pier'),
      rear: () => t('types.unitTypes.rear'),
      room: () => t('types.unitTypes.room'),
      side: () => t('types.unitTypes.side'),
      space: () => t('types.unitTypes.space'),
      stop: () => t('types.unitTypes.stop'),
      suite: () => t('types.unitTypes.suite'),
      trailer: () => t('types.unitTypes.trailer'),
      unit: () => t('types.unitTypes.unit'),
      upper: () => t('types.unitTypes.upper'),
    },

    propertyUsageTypes: {
      primaryResidence: () => t('types.propertyUsageTypes.primaryResidence'),
      secondHome: () => t('types.propertyUsageTypes.secondHome'),
      investor: () => t('types.propertyUsageTypes.investor'),
      fhaSecondaryResidence: () =>
        t('types.propertyUsageTypes.fhaSecondaryResidence'),
    },

    priorPropertyUsageTypes: {
      primaryResidence: () =>
        t('types.priorPropertyUsageTypes.primaryResidence'),
      fhaSecondaryResidence: () =>
        t('types.priorPropertyUsageTypes.fhaSecondaryResidence'),
      secondaryResidence: () =>
        t('types.priorPropertyUsageTypes.secondaryResidence'),
      investment: () => t('types.priorPropertyUsageTypes.investment'),
    },

    priorPropertyTitleTypes: {
      sole: () => t('types.priorPropertyTitleTypes.sole'),
      jointWithSpouse: () => t('types.priorPropertyTitleTypes.jointWithSpouse'),
      jointWithOtherThanSpouse: () =>
        t('types.priorPropertyTitleTypes.jointWithOtherThanSpouse'),
    },

    maritalStatusTypes: {
      married: () => t('types.maritalStatusTypes.married'),
      unmarried: () => t('types.maritalStatusTypes.unmarried'),
      separated: () => t('types.maritalStatusTypes.separated'),
    },

    residencyStatusTypes: {
      usCitizen: () => t('types.residencyStatusTypes.usCitizen'),
      permanentResidentAlien: () =>
        t('types.residencyStatusTypes.permanentResidentAlien'),
      nonPermanentResidentAlien: () =>
        t('types.residencyStatusTypes.nonPermanentResidentAlien'),
    },

    borrowerTypes: {
      borrower: () => t('types.borrowerTypes.borrower'),
      coBorrower: () => t('types.borrowerTypes.coBorrower'),
      both: () => t('types.borrowerTypes.both'),
    },

    consentTypes: {
      creditConsent: () => t('types.consentTypes.creditConsent'),
      eConsent: () => t('types.consentTypes.eConsent'),
    },

    consentActions: {
      accepted: () => t('types.consentActions.accepted'),
      rejected: () => t('types.consentActions.rejected'),
    },

    loanProductDataGsePropertyTypes: {
      attached: () => t('types.loanProductDataGsePropertyTypes.attached'),
      condominium: () => t('types.loanProductDataGsePropertyTypes.condominium'),
      cooperative: () => t('types.loanProductDataGsePropertyTypes.cooperative'),
      detached: () => t('types.loanProductDataGsePropertyTypes.detached'),
      highRiseCondominium: () =>
        t('types.loanProductDataGsePropertyTypes.highRiseCondominium'),
      manufacturedHousing: () =>
        t('types.loanProductDataGsePropertyTypes.manufacturedHousing'),
      pud: () => t('types.loanProductDataGsePropertyTypes.pud'),
      detachedCondo: () =>
        t('types.loanProductDataGsePropertyTypes.detachedCondo'),
      manufacturedHomeCondoPUDCoOp: () =>
        t('types.loanProductDataGsePropertyTypes.manufacturedHomeCondoPUDCoOp'),
      mhSelect: () => t('types.loanProductDataGsePropertyTypes.mhSelect'),
      mhAdvantage: () => t('types.loanProductDataGsePropertyTypes.mhAdvantage'),
    },
  };
};

/**
 * Static helper functions for getting translation keys without React hooks
 * Use these when you need the translation key itself, not the translated value
 */
export const getTypeValidationKeys = () => ({
  field: {
    refinancingCurrentProperty:
      'types.validation.field.refinancingCurrentProperty',
    hasRealtor: 'types.validation.field.hasRealtor',
    needsRealtor: 'types.validation.field.needsRealtor',
    hasOwnershipInterest: 'types.validation.field.hasOwnershipInterest',
    sameAddressAsBorrower: 'types.validation.field.sameAddressAsBorrower',
    sameMailingAddressAsBorrower:
      'types.validation.field.sameMailingAddressAsBorrower',
    hasCoborrowerOwnershipInterest:
      'types.validation.field.hasCoborrowerOwnershipInterest',
  },
  loan: {
    idealPurchasePrice: 'types.validation.loan.idealPurchasePrice',
    mortgageType: 'types.validation.loan.mortgageType',
    borrowerRequestedLoanAmount:
      'types.validation.loan.borrowerRequestedLoanAmount',
    cashOutBalance: 'types.validation.loan.cashOutBalance',
    downPaymentAmount: 'types.validation.loan.downPaymentAmount',
    downPaymentSourceDescription:
      'types.validation.loan.downPaymentSourceDescription',
    downPaymentPercent: 'types.validation.loan.downPaymentPercent',
    purchasePriceAmount: 'types.validation.loan.purchasePriceAmount',
    loanAmortizationType: 'types.validation.loan.loanAmortizationType',
    loanAmortizationTermMonths:
      'types.validation.loan.loanAmortizationTermMonths',
    propertyUsageType: 'types.validation.loan.propertyUsageType',
    fnmProductPlanIdentifier: 'types.validation.loan.fnmProductPlanIdentifier',
  },
  property: {
    addressUnitDesignatorType:
      'types.validation.property.addressUnitDesignatorType',
    addressUnitIdentifier: 'types.validation.property.addressUnitIdentifier',
    addressLineText: 'types.validation.property.addressLineText',
    city: 'types.validation.property.city',
    state: 'types.validation.property.state',
    postalCode: 'types.validation.property.postalCode',
    county: 'types.validation.property.county',
    financedNumberOfUnits: 'types.validation.property.financedNumberOfUnits',
    refinancePropertyAcquiredYear:
      'types.validation.property.refinancePropertyAcquiredYear',
    loanPurposeType: 'types.validation.property.loanPurposeType',
    refinancePropertyExistingLienAmount:
      'types.validation.property.refinancePropertyExistingLienAmount',
  },
  loanProductData: {
    gsePropertyType: 'types.validation.loanProductData.gsePropertyType',
  },
});

/**
 * Utility function to translate error messages that might be translation keys
 * Use this in components to translate validation error messages
 */
export const useTranslateErrorMessage = () => {
  const { t } = useI18n();

  return (message: string | undefined): string | undefined => {
    if (!message) return message;

    // Check if the message looks like a translation key
    if (message.startsWith('types.validation.')) {
      const translated = t(message, message); // Use the key as fallback if translation not found
      return translated;
    }

    return message;
  };
};
